import { useEffect } from "react";
import type { Employee } from "@/types/db";

type Theme = "system" | "light" | "dark";

const STORAGE_KEY = "scenta-hq-theme";

function resolve(pref: Theme): "light" | "dark" {
  if (pref === "system") {
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return pref;
}

function apply(pref: Theme) {
  const html = document.documentElement;
  if (resolve(pref) === "dark") html.classList.add("dark");
  else html.classList.remove("dark");
}

export function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "system";
  const v = localStorage.getItem(STORAGE_KEY);
  if (v === "light" || v === "dark" || v === "system") return v;
  return "system";
}

export function setTheme(pref: Theme) {
  localStorage.setItem(STORAGE_KEY, pref);
  apply(pref);
}

export function useThemeSync(employee: Employee | null) {
  useEffect(() => {
    const stored = readStoredTheme();
    const pref: Theme = employee?.theme_preference ?? stored;
    apply(pref);
    if (employee?.theme_preference && employee.theme_preference !== stored) {
      localStorage.setItem(STORAGE_KEY, employee.theme_preference);
    }
    const mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    const onChange = () => apply(readStoredTheme());
    mql?.addEventListener?.("change", onChange);
    return () => mql?.removeEventListener?.("change", onChange);
  }, [employee?.theme_preference]);
}
